import { useEffect, useState } from 'react';
import { Taskbar } from './components/shell/Taskbar';
import { Sidebar } from './components/shell/Sidebar';
import { BentoLayout } from './components/shell/BentoLayout';
import { SettingsModal, ExportModal, LLMModal } from './components/modals';
import { useAppState } from './core/state';
import { apiClient } from './core/apiClient';
import type { Bridge, BridgeListResponse } from './contracts/bridge';

function App() {
  const { state, actions } = useAppState();
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [llmOpen, setLLMOpen] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    loadBridges();
  }, []);

  const loadBridges = async () => {
    actions.setLoading(true);
    try {
      const response = await apiClient.get<BridgeListResponse>('/bridges/');
      actions.setBridges(response.bridges);
    } catch (error) {
      actions.setError('Failed to load bridges');
    } finally {
      actions.setLoading(false);
    }
  };

  const handleBridgeSelect = (bridge: Bridge) => {
    actions.setSelectedBridge(bridge);
  };

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  return (
    <div className="app-container">
      <Taskbar
        selectedBridge={state.selectedBridge}
        onThemeToggle={toggleTheme}
        theme={theme}
        onSettingsClick={() => setSettingsOpen(true)}
        onExportClick={() => setExportOpen(true)}
        onLLMClick={() => setLLMOpen(true)}
      />
      <div className="main-content">
        <Sidebar
          bridges={state.bridges}
          selectedBridge={state.selectedBridge}
          onBridgeSelect={handleBridgeSelect}
          isLoading={state.isLoading}
        />
        <BentoLayout
          bridge={state.selectedBridge}
          forecast={state.forecast}
          currentRisk={state.currentRisk}
          mitigation={state.mitigation}
        />
      </div>

      <SettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
      <ExportModal
        isOpen={exportOpen}
        onClose={() => setExportOpen(false)}
        bridgeId={state.selectedBridge?.bridge_id}
      />
      <LLMModal
        isOpen={llmOpen}
        onClose={() => setLLMOpen(false)}
        bridgeId={state.selectedBridge?.bridge_id}
      />
    </div>
  );
}

export default App;
